<template>
  <div class="private-consulting-page" dir="rtl">
    <SingleRideHeader :presentedText="consultingInfo.heroTitle || t('consultingHeroBinding')" />
    <b-container>
      <div class="consulting-content after_page_header">
        <!-- Back Button -->
        <div class="consulting-back-button-container">
          <router-link :to="backToFeaturedRides" class="consulting-back-button">
            <i class="fa-solid fa-arrow-right"></i>
            {{ consultingInfo.backButtonText || t('consultingBackToRecommended') }}
          </router-link>
        </div>

        <!-- Hero Section -->
        <section class="consulting-hero">
          <div class="consulting-hero-text">
            <h1>{{ consultingInfo.heroTitle }}</h1>
            <p>{{ consultingInfo.heroDescription1 }}</p>
            <p>{{ consultingInfo.heroDescription2 }}</p>
          </div>
          
          <!-- WhatsApp Contact Button in Hero -->
          <div v-if="consultingInfo.whatsappNumber" class="consulting-top-whatsapp-container">
            <b-button 
              variant="success" 
              class="consulting-whatsapp-btn"
              @click="openWhatsApp"
              :aria-label="consultingInfo.ctaButtonText || t('consultingCtaContactBinding')"
            >
              <svg class="whatsapp-icon" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
              </svg>
              {{ consultingInfo.ctaButtonText || t('consultingCtaContactPlural') }}
            </b-button>
          </div>
        </section>

        <!-- What You Get Section -->
        <section class="consulting-section">
          <h2>{{ consultingInfo.whatYouGetTitle }}</h2>
          <div class="consulting-grid">
            <div v-for="(card, index) in consultingInfo.cards" :key="index" class="consulting-card">
              <div class="consulting-card-icon">
                <i :class="card.icon"></i>
              </div>
              <h3>{{ card.title }}</h3>
              <p>{{ card.description }}</p>
            </div>
          </div>
        </section>

        <!-- Process Section -->
        <section class="consulting-section">
          <h2>{{ consultingInfo.processTitle }}</h2>
          <div class="consulting-process">
            <div v-for="(step, index) in consultingInfo.processSteps" :key="index" class="consulting-step">
              <div class="step-number">{{ index + 1 }}</div>
              <div class="step-content">
                <h3>{{ step.title }}</h3>
                <p>{{ step.description }}</p>
              </div>
            </div>
          </div>
        </section>

        <!-- Why Choose Section -->
        <section class="consulting-section" v-if="consultingInfo.whyChooseTitle">
          <h2>{{ consultingInfo.whyChooseTitle }}</h2>
          <div class="consulting-benefits">
            <div v-for="(benefit, index) in consultingInfo.benefits" :key="index" class="consulting-benefit">
              <i class="fa-solid fa-check-circle"></i>
              <p>{{ benefit }}</p>
            </div>
          </div>
        </section>

        <!-- Additional Info Section -->
        <section class="consulting-section consulting-info-note" v-if="consultingInfo.additionalInfoTitle && consultingInfo.additionalInfo">
          <div class="info-note-content">
            <i class="fa-solid fa-info-circle"></i>
            <div class="info-note-text">
              <h3>{{ consultingInfo.additionalInfoTitle }}</h3>
              <p>{{ consultingInfo.additionalInfo }}</p>
            </div>
          </div>
        </section>

        <!-- CTA Section -->
        <section class="consulting-cta" v-if="consultingInfo.whatsappNumber">
          <h2>{{ consultingInfo.ctaTitle }}</h2>
          <p class="cta-description">{{ consultingInfo.ctaDescription }}</p>
          <b-button 
            variant="success" 
            class="consulting-whatsapp-btn"
            @click="openWhatsApp"
            :aria-label="consultingInfo.ctaButtonText || t('consultingCtaWhatsAppBinding')"
          >
            <svg class="whatsapp-icon" viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
            </svg>
            {{ consultingInfo.ctaButtonText || t('consultingCtaContactPlural') }}
          </b-button>
        </section>

        <!-- Back Button at Bottom -->
        <div class="consulting-back-button-container bottom-back-button">
          <router-link :to="backToFeaturedRides" class="consulting-back-button">
            <i class="fa-solid fa-arrow-right"></i>
            {{ consultingInfo.backButtonText || t('consultingBackToRecommended') }}
          </router-link>
        </div>
      </div>
    </b-container>
    <PageFooter
      v-if="!isSubdomain"
    />
  </div>
</template>

<script>
import PageFooter from "@/components/PageFooter.vue";
import SingleRideHeader from "@/components/SingleRideHeader";
import config from "@/common/config";
import { mapGetters } from 'vuex';
import { translationMixin } from "@/mixins/translationMixin";

export default {
  name: 'PrivateConsultingInfo',
  mixins: [translationMixin],
  components: {
    PageFooter,
    SingleRideHeader
  },
  computed: {
    ...mapGetters("theme", ["isSubdomain"]),
    consultingInfo() {
      // Get manager from route query, params, or find from FEATURED_RIDES
      let managerUrlName = this.$route.query.manager || this.$route.params.managerUrlName || null;
      
      // If no manager in route, try to find from FEATURED_RIDES based on current route
      if (!managerUrlName) {
        const featuredRides = config.config.FEATURED_RIDES || [];
        const currentPath = this.$route.path;
        
        // Check if we're on a featured rides page
        if (currentPath.includes('/featured-rides')) {
          const pathParts = currentPath.split('/');
          const pathManager = pathParts[pathParts.length - 1];
          
          // Find matching manager
          const matchingManager = featuredRides.find(manager => 
            manager.managerUrlName === pathManager || 
            manager.slug === pathManager ||
            (manager.isDefault && pathManager === 'featured-rides')
          );
          
          if (matchingManager) {
            managerUrlName = matchingManager.managerUrlName;
          }
        }
      }
      
      // Get consulting info for this manager, or use default
      // PRIVATE_CONSULTING_INFO is nested inside each guide section in DIGITAL_GUIDE_INFO
      const digitalGuideInfo = config.config.DIGITAL_GUIDE_INFO || {};
      
      // Get the guide info for this manager (or default)
      const guideInfo = managerUrlName && digitalGuideInfo[managerUrlName] 
        ? digitalGuideInfo[managerUrlName] 
        : digitalGuideInfo.default || {};
      
      // Get the PRIVATE_CONSULTING_INFO from the guide
      const managerConsultingInfo = guideInfo.PRIVATE_CONSULTING_INFO || null;
      
      // Get default consulting info
      const defaultConsultingInfo = digitalGuideInfo.default?.PRIVATE_CONSULTING_INFO || {};
      
      // Merge manager-specific consulting info with default (manager overrides default)
      return managerConsultingInfo ? { ...defaultConsultingInfo, ...managerConsultingInfo } : defaultConsultingInfo;
    },
    backToFeaturedRides() {
      const managerUrlName = this.$route.query.manager || this.$route.params.managerUrlName || null;
      
      // If we have a manager, go to their specific featured rides page
      if (managerUrlName) {
        const featuredRides = config.config.FEATURED_RIDES || [];
        const manager = featuredRides.find(m => m.managerUrlName === managerUrlName);
        
        if (manager && manager.slug) {
          return {
            name: 'featured-rides-landing-manager',
            params: { managerUrlName: manager.slug }
          };
        } else if (manager && manager.managerUrlName) {
          return {
            name: 'featured-rides-landing-manager',
            params: { managerUrlName: manager.managerUrlName }
          };
        }
      }
      
      // Default: go to main featured rides page
      return { name: 'featured-rides-landing' };
    }
  },
  methods: {
    openWhatsApp() {
      if (!this.consultingInfo || !this.consultingInfo.whatsappNumber) {
        return;
      }
      const message = this.consultingInfo.whatsappMessage || this.t('consultingWhatsAppMessage');
      const encodedMessage = encodeURIComponent(message);
      const url = `https://wa.me/${this.consultingInfo.whatsappNumber}?text=${encodedMessage}`;
      window.open(url, '_blank', 'noopener');
    }
  }
};
</script>

<style scoped>
.private-consulting-page {
  font-family: 'Noto Sans Hebrew', sans-serif;
  background: #f8f9fa;
}

.consulting-content {
  max-width: 1200px;
  margin: 0 auto;
  padding: 4rem 2rem 6rem;
}

.consulting-back-button-container {
  margin-bottom: 3rem;
}

.consulting-back-button-container.bottom-back-button {
  margin-top: 4rem;
  margin-bottom: 2rem;
  text-align: center;
}

.consulting-back-button {
  display: inline-flex;
  align-items: center;
  gap: 0.8rem;
  color: #07A0C3;
  font-size: 1.8rem;
  text-decoration: none;
  padding: 1rem 2rem;
  border: 2px solid #07A0C3;
  border-radius: 8px;
  transition: all 0.3s ease;
  font-weight: 500;
}

.consulting-back-button:hover {
  background: linear-gradient(45deg, #34D0B6, #07A0C3);
  color: #fff;
  border-color: transparent;
  text-decoration: none;
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(7, 160, 195, 0.3);
}

.consulting-back-button i {
  font-size: 1.4rem;
}

.consulting-top-whatsapp-container {
  text-align: center;
  margin-bottom: 3rem;
}

/* Hero */
.consulting-hero {
  text-align: right;
  margin-bottom: 4rem;
}

.consulting-hero-text h1 {
  font-size: 3rem;
  color: #0d2f29;
  margin-bottom: 2rem;
  font-weight: bold;
}

.consulting-hero-text p {
  font-size: 1.8rem;
  color: #495057;
  line-height: 1.9;
  margin-bottom: 1.5rem;
}

/* Section */
.consulting-section {
  background: #ffffff;
  padding: 4rem;
  border-radius: 20px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
  margin-bottom: 3rem;
  text-align: right;
}

.consulting-section h2 {
  font-size: 2.4rem;
  color: #0d2f29;
  margin-bottom: 3rem;
  font-weight: bold;
  text-align: right;
  position: relative;
  padding-bottom: 1rem;
}

.consulting-section h2::after {
  content: '';
  position: absolute;
  bottom: 0;
  right: 0;
  width: 80px;
  height: 4px;
  background: linear-gradient(45deg, #34D0B6, #07A0C3);
  border-radius: 2px;
}

/* Cards Grid */
.consulting-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2.5rem;
}

.consulting-card {
  background: linear-gradient(135deg, rgba(7, 160, 195, 0.08) 0%, rgba(52, 208, 182, 0.08) 100%);
  padding: 2.5rem;
  border-radius: 16px;
  border-right: 4px solid #07A0C3;
  text-align: right;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.consulting-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 20px rgba(7, 160, 195, 0.2);
}

.consulting-card-icon {
  font-size: 3rem;
  color: #07A0C3;
  margin-bottom: 1.5rem;
}

.consulting-card h3 {
  font-size: 1.8rem;
  color: #0d2f29;
  margin-bottom: 1rem;
  font-weight: 600;
}

.consulting-card p {
  font-size: 1.6rem;
  color: #495057;
  line-height: 1.8;
  margin: 0;
}

/* Process Steps */
.consulting-process {
  display: flex;
  flex-direction: column;
  gap: 2.5rem;
}

.consulting-step {
  display: flex;
  align-items: flex-start;
  gap: 2rem;
  text-align: right;
}

.step-number {
  flex-shrink: 0;
  width: 60px;
  height: 60px;
  background: linear-gradient(45deg, #34D0B6, #07A0C3);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  font-weight: bold;
  box-shadow: 0 4px 12px rgba(7, 160, 195, 0.3);
}

.step-content {
  flex: 1;
}

.step-content h3 {
  font-size: 2rem;
  color: #0d2f29;
  margin-bottom: 1rem;
  font-weight: 600;
}

.step-content p {
  font-size: 1.6rem;
  color: #495057;
  line-height: 1.8;
  margin: 0;
}

/* Benefits */
.consulting-benefits {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.consulting-benefit {
  display: flex;
  align-items: flex-start;
  gap: 1.5rem;
  text-align: right;
}

.consulting-benefit i {
  flex-shrink: 0;
  font-size: 2rem;
  color: #34D0B6;
  margin-top: 0.3rem;
}

.consulting-benefit p {
  font-size: 1.8rem;
  color: #495057;
  line-height: 1.8;
  margin: 0;
}

/* Additional Info Note */
.consulting-info-note {
  background: linear-gradient(135deg, rgba(255, 193, 7, 0.1) 0%, rgba(255, 152, 0, 0.1) 100%);
  border: 2px solid rgba(255, 193, 7, 0.3);
}

.info-note-content {
  display: flex;
  align-items: flex-start;
  gap: 1.5rem;
  text-align: right;
}

.info-note-content i {
  flex-shrink: 0;
  font-size: 2.5rem;
  color: #ff9800;
  margin-top: 0.3rem;
}

.info-note-text h3 {
  font-size: 1.8rem;
  color: #0d2f29;
  margin-bottom: 1rem;
  font-weight: 600;
}

.info-note-text p {
  font-size: 1.6rem;
  color: #495057;
  line-height: 1.8;
  margin: 0;
}

/* CTA Section */
.consulting-cta {
  background: linear-gradient(135deg, rgba(7, 160, 195, 0.1) 0%, rgba(52, 208, 182, 0.1) 100%);
  padding: 4rem;
  border-radius: 20px;
  text-align: center;
  border: 2px solid rgba(7, 160, 195, 0.2);
}

.consulting-cta h2 {
  font-size: 2.4rem;
  color: #0d2f29;
  margin-bottom: 1.5rem;
  text-align: center;
}

.consulting-cta h2::after {
  right: 50%;
  transform: translateX(50%);
}

.cta-description {
  font-size: 1.8rem;
  color: #495057;
  line-height: 1.8;
  margin-bottom: 2.5rem;
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
}

.consulting-whatsapp-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.8rem;
  padding: 1.4rem 3rem;
  font-size: 1.8rem;
  font-weight: 600;
  border-radius: 12px;
  background: linear-gradient(45deg, #34D0B6, #07A0C3);
  border: none;
  color: white;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(7, 160, 195, 0.3);
}

.consulting-whatsapp-btn:hover {
  background: linear-gradient(45deg, #07A0C3, #34D0B6);
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(7, 160, 195, 0.4);
  color: white;
}

.whatsapp-icon {
  width: 24px;
  height: 24px;
  flex-shrink: 0;
}

@media (max-width: 768px) {
  .consulting-content {
    padding: 2rem 1rem 4rem;
  }

  .consulting-hero-text h1 {
    font-size: 2.2rem;
  }

  .consulting-hero-text p {
    font-size: 1.6rem;
  }

  .consulting-section {
    padding: 2.5rem 1.5rem;
  }

  .consulting-section h2 {
    font-size: 2rem;
  }

  .consulting-grid {
    grid-template-columns: 1fr;
    gap: 2rem;
  }

  .consulting-card {
    padding: 2rem;
  }

  .consulting-step {
    flex-direction: column;
    align-items: flex-start;
  }

  .step-number {
    width: 50px;
    height: 50px;
    font-size: 1.6rem;
  }

  .consulting-cta {
    padding: 2.5rem 1.5rem;
  }

  .consulting-whatsapp-btn {
    font-size: 1.6rem;
    padding: 1.2rem 2.4rem;
  }
}
</style>

